import { ChevronLeftIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-left'
import { Pin02Icon } from '@repo/studio-design-system-ui-icons/general/pin-02'
import { XCloseIcon } from '@repo/studio-design-system-ui-icons/general/x-close'
import type {
  AppSwitchBarItem,
  AppSwitchCluster,
} from '@repo/studio-components/top-app-bar/components/app-switch-bar'
import * as Popover from 'primitives/popover'
import * as React from 'react'
import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { tw } from 'utils/classnames'
import { NavigationItem } from './navigation-item'
import { useClusterDefinitions } from './navigation-cluster-data'
import type { Cluster } from './navigation-cluster-data'
import { NavigationClusterPopover } from './navigation-cluster'
import { Button } from '@repo/studio-design-system-ui-button'

type PinnedTools = Partial<Record<AppSwitchCluster, AppSwitchBarItem[]>>
export function PinnedToolsMenu({
  expanded,
  onNavigate,
  pinnedTools,
}: {
  expanded: boolean
  onNavigate?: () => void
  pinnedTools: PinnedTools
}) {
  const t = useStudioTranslate()

  return (
    <Popover.Root
      behaviour="hover"
      focusBehaviour="none"
      blockPointerEvents
      safePolygonBulge={18}
      safePolygonRequireIntent={false}
      safePolygonRestMs={500}
    >
      <NavigationClusterPopover
        label={t('studio.clusters.pinned')}
        icon={<Pin02Icon />}
        expanded={expanded}
        alignOffset={-91}
      >
        <div className="w-full pb-1">
          <span className="ml-2 typography-16 text-gray-50">{t('studio.clusters.pinned')}</span>
        </div>
        <PinnedToolsMenuContent pinnedTools={pinnedTools} onNavigate={onNavigate} />
      </NavigationClusterPopover>
    </Popover.Root>
  )
}

export function PinnedToolsSideSection({
  pinnedTools,
  onNavigate,
  onClose,
}: {
  pinnedTools: PinnedTools
  onNavigate?: () => void
  onClose?: () => void
}) {
  const t = useStudioTranslate()
  return (
    <div
      className={tw(
        'absolute left-0 w-dvw top-0 h-dvh bg-gray-1000 p-3 z-studio-toaster',
        'md:left-[245px] md:w-[calc(100dvw-245px)] md:p-4',
      )}
    >
      <div className="w-full pb-3 flex justify-between items-center gap-2">
        <div className="flex items-center gap-2">
          <Button
            iconOnly
            aria-label="Close pinned tools"
            size="xs"
            variant="secondary"
            onClick={onClose}
          >
            <ChevronLeftIcon />
          </Button>
          <span className="ml-2 typography-16 text-gray-50">{t('studio.clusters.pinned')}</span>
        </div>
        <Button iconOnly aria-label="Close" size="xs" variant="ghost" onClick={onClose}>
          <XCloseIcon />
        </Button>
      </div>
      <PinnedToolsMenuContent pinnedTools={pinnedTools} onNavigate={onNavigate} />
    </div>
  )
}

function PinnedToolsMenuContent({
  pinnedTools,
  onNavigate,
}: {
  pinnedTools: PinnedTools
  onNavigate?: () => void
}) {
  const clusterDefinitions = useClusterDefinitions()

  const clustersWithPinnedTools = React.useMemo((): Cluster[] => {
    return Object.entries(pinnedTools).flatMap(([cluster, items]) => {
      const definition = clusterDefinitions.find(
        def => def.cluster === (cluster as AppSwitchCluster),
      )
      if (!definition || !items || items.length === 0) {
        return []
      }
      return [
        {
          ...definition,
          items: items.toSorted((a, b) => a.order - b.order),
          navigationOnlyItems: [],
        },
      ]
    })
  }, [pinnedTools, clusterDefinitions])

  const clusterCount = clustersWithPinnedTools.length
  if (clusterCount === 0) {
    return null
  }

  return (
    <>
      <div className={tw('grid gap-2', clusterCount > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
        {clustersWithPinnedTools.map(cluster => (
          <div key={cluster.cluster} className="flex flex-col gap-1">
            <div className="w-full border-b border-gray-900 pb-1">
              <span className="ml-2 typography-12 text-gray-500">{cluster.label}</span>
            </div>
            <div className="flex flex-col gap-1 mt-2">
              {/* hide the icon on mobile, we really do not have space for it */}
              {cluster.items.map(item => (
                <NavigationItem
                  className="[&_svg[data-navigation-icon]]:hidden md:[&_svg[data-navigation-icon]]:block"
                  key={item.title}
                  title={item.title}
                  route={item.route}
                  expanded={true}
                  icon={item.icon}
                  onNavigate={onNavigate}
                  tool={item}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
